import { useEffect, useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Check, X as XIcon, CheckCircle2, XCircle, LifeBuoy, Send, Sprout } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import Messages from "@/components/Messages";
import ReportUserDialog from "@/components/ReportUserDialog";
import VerifyFundsDialog from "@/components/VerifyFundsDialog";
import AdminCreateUserDialog from "@/components/AdminCreateUserDialog";
import GrantMembershipDialog from "@/components/GrantMembershipDialog";
import AdminChangePasswordDialog from "@/components/AdminChangePasswordDialog";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [wholesale, setWholesale] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reports, setReports] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [driverApps, setDriverApps] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [activity, setActivity] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  const [newFarmers, setNewFarmers] = useState([]);
  const [replyTexts, setReplyTexts] = useState({}); // ticketId -> draft string
  const [sending, setSending] = useState(false);

  const loadAll = () => {
    Promise.all([
      api.get("/admin/users"),
      api.get("/wholesale"),
      api.get("/contact"),
      api.get("/orders"),
      api.get("/admin/reports"),
      api.get("/admin/testimonials"),
      api.get("/admin/driver-applications"),
      api.get("/admin/ledger"),
      api.get("/admin/activity-feed"),
      api.get("/admin/support/tickets"),
      api.get("/admin/new-farmers"),
    ]).then(([u, w, c, o, r, t, d, l, a, s, nf]) => {
      setUsers(u.data); setWholesale(w.data); setContactMessages(c.data);
      setOrders(o.data); setReports(r.data); setTestimonials(t.data);
      setDriverApps(d.data); setLedger(l.data); setActivity(a.data);
      setSupportTickets(s.data); setNewFarmers(nf.data);
    }).catch(() => toast.error("Failed to load admin data"));
  };

  useEffect(() => { loadAll(); }, []);

  const approveTestimonial = async (id) => {
    await api.post(`/admin/testimonials/${id}/approve`);
    toast.success("Approved.");
    loadAll();
  };
  const deleteTestimonial = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Deleted.");
    loadAll();
  };
  const approveDriver = async (uid) => {
    try {
      await api.post(`/admin/driver-applications/${uid}/approve`);
      toast.success("Driver approved.");
      loadAll();
    } catch (err) {
      toast.error(extractApiError(err, "Could not approve driver."));
    }
  };
  const rejectDriver = async (uid) => {
    try {
      await api.post(`/admin/driver-applications/${uid}/reject`);
      toast.success("Driver application rejected.");
      loadAll();
    } catch (err) {
      toast.error(extractApiError(err, "Could not reject driver."));
    }
  };

  const sendSupportReply = async (ticketId) => {
    const text = (replyTexts[ticketId] || "").trim();
    if (!text) {
      toast.error("Type a reply first.");
      return;
    }
    try {
      await api.post(`/admin/support/tickets/${ticketId}/reply`, { message: text });
      toast.success("Reply sent.");
      setReplyTexts((r) => ({ ...r, [ticketId]: "" }));
      loadAll();
    } catch (err) {
      toast.error(extractApiError(err, "Could not send reply."));
    }
  };
  const closeSupport = async (ticketId) => {
    try {
      await api.post(`/admin/support/tickets/${ticketId}/close`);
      toast.success("Ticket closed.");
      loadAll();
    } catch (err) {
      toast.error(extractApiError(err, "Could not close ticket."));
    }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/broadcast");
      if (data.sent > 0) toast.success(`Digest emailed to ${data.sent} member${data.sent !== 1 ? "s" : ""}.`);
      else if (!data.configured) toast.error("RESEND_API_KEY not configured.");
      else toast.message("No unread alerts to send.");
    } catch {
      toast.error("Could not send digest.");
    } finally {
      setSending(false);
    }
  };

  const verifyFarmer = async (uid) => {
    try {
      await api.post(`/admin/farmers/${uid}/verify`);
      toast.success("Hobby farmer verified.");
      loadAll();
    } catch (err) {
      toast.error(extractApiError(err, "Could not verify farmer."));
    }
  };

  const stat = (label, value, testid) => (
    <div data-testid={testid} className="bg-white border border-stone-200 p-6 rounded-md">
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</div>
      <div className="mt-3 font-serif text-3xl text-stone-900">{value}</div>
    </div>
  );

  const pendingDrivers = driverApps.filter((a) => a.status === "submitted").length;
  const unverifiedOrders = orders.filter((o) => !o.funds_verified).length;
  const openSupport = supportTickets.filter((t) => t.status !== "closed").length;
  const pendingFarmers = newFarmers.filter((f) => f.needs_verification).length;
  const newFarmersThisWeek = newFarmers.filter((f) => f.is_new).length;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {stat("Users", users.length, "stat-users")}
        {stat("Orders", orders.length, "stat-admin-orders")}
        {stat("New Farmers", newFarmersThisWeek, "stat-new-farmers")}
        {stat("Driver Apps", pendingDrivers, "stat-driver-apps")}
        {stat("Reports", reports.length, "stat-reports")}
        {stat("Support", openSupport, "stat-support")}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <AdminCreateUserDialog onCreated={loadAll} />
        <AdminChangePasswordDialog />
        <Button data-testid="seed-demo-btn" variant="outline" onClick={async () => {
          try {
            const { data } = await api.post("/admin/seed-featured-farms");
            toast.success(`Seeded ${data.created_farms} farms + ${data.created_products} products.`);
            loadAll();
          } catch (err) {
            toast.error(extractApiError(err, "Could not seed demo farms."));
          }
        }} className="border-emerald-700/40 text-emerald-800 hover:bg-emerald-50">
          Seed Demo Farms
        </Button>
        <Button data-testid="send-digest-btn" onClick={sendDigest} disabled={sending} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
          <Mail size={16} className="mr-2" /> {sending ? "Sending..." : "Email Digest to All Members"}
        </Button>
        <div className="text-xs text-stone-500 font-sans">
          Sends unread-alerts digest. Requires <code className="bg-stone-100 px-1 rounded">RESEND_API_KEY</code>.
        </div>
      </div>

      <Tabs defaultValue={typeof window !== "undefined" && window.location.search.includes("tab=farmers") ? "farmers" : "orders"}>
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-stone-100 flex-nowrap whitespace-nowrap w-max sm:w-auto">
            <TabsTrigger data-testid="tab-admin-orders" value="orders">Orders</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-farmers" value="farmers">
              New Farmers{pendingFarmers > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-700 text-stone-50 text-[10px] font-semibold">{pendingFarmers}</span>}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-admin-support" value="support">
              Support{openSupport > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-700 text-stone-50 text-[10px] font-semibold">{openSupport}</span>}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-admin-activity" value="activity">Activity</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-drivers" value="drivers">Drivers</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-ledger" value="ledger">Ledger</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-users" value="users">Users</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-reports" value="reports">Reports</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-wh" value="wholesale">Wholesale</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-contact" value="contact-messages">Contact</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-testimonials" value="testimonials">Testimonials</TabsTrigger>
            <TabsTrigger data-testid="tab-admin-msgs" value="messages">Inbox</TabsTrigger>
          </TabsList>
        </div>

        {/* ORDERS — with Verify Funds */}
        <TabsContent value="orders" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 uppercase text-stone-600 tracking-[0.15em] text-xs">
                <tr>
                  <th className="p-4 text-left">Product</th>
                  <th className="p-4 text-left">Farmer</th>
                  <th className="p-4 text-left">Client</th>
                  <th className="p-4 text-left">Driver</th>
                  <th className="p-4 text-left">Status</th>
                  <th className="p-4 text-left">Funds</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-stone-500 font-sans">No orders yet.</td></tr>
                ) : orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-stone-100" data-testid={`order-row-${o.order_id}`}>
                    <td className="p-4 font-sans">{o.product_title}</td>
                    <td className="p-4 font-sans text-stone-600">{o.farmer_name}</td>
                    <td className="p-4 font-sans text-stone-600">{o.client_name}</td>
                    <td className="p-4 font-sans text-stone-600">{o.driver_name || "—"}</td>
                    <td className="p-4"><Badge className="bg-stone-800 text-stone-50">{o.status}</Badge></td>
                    <td className="p-4">
                      <Badge className={o.funds_verified ? "bg-emerald-800 text-stone-50" : "bg-amber-700 text-stone-50"}>
                        {o.funds_verified ? "Verified" : "Pending"}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <VerifyFundsDialog order={o} onSuccess={loadAll} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* NEW FARMERS — dedicated tab for reviewing hobby farmer signups */}
        <TabsContent value="farmers" className="mt-6 space-y-4" data-testid="tab-content-farmers">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
            <Sprout className="text-emerald-800 shrink-0 mt-0.5" size={20} />
            <div className="text-sm font-sans text-emerald-900">
              <div className="font-semibold">New hobby farmer signups</div>
              <div className="text-emerald-800/80 mt-1">
                Review each farmer&apos;s uploaded identification before they can list produce. Newest signups appear first.
                {pendingFarmers > 0 && <span className="ml-1 font-semibold">{pendingFarmers} awaiting verification.</span>}
              </div>
            </div>
          </div>

          {newFarmers.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500">
              No hobby farmers have signed up yet. When someone completes the farmer signup form, they&apos;ll appear here for you to verify.
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 uppercase text-stone-600 tracking-[0.15em] text-xs">
                  <tr>
                    <th className="p-4 text-left">Farmer</th>
                    <th className="p-4 text-left">Contact</th>
                    <th className="p-4 text-left">State</th>
                    <th className="p-4 text-left">Signed up</th>
                    <th className="p-4 text-left">Products</th>
                    <th className="p-4 text-left">ID</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {newFarmers.map((f) => (
                    <tr
                      key={f.user_id}
                      data-testid={`new-farmer-row-${f.user_id}`}
                      className={`border-t border-stone-100 ${f.is_new ? "bg-amber-50/40" : ""}`}
                    >
                      <td className="p-4 font-serif text-stone-900">
                        <div className="flex items-center gap-2">
                          {f.name || "(no name)"}
                          {f.is_new && (
                            <span className="text-[10px] uppercase tracking-[0.15em] bg-amber-700 text-stone-50 px-1.5 py-0.5 rounded">
                              new
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-stone-600 font-sans text-xs">{f.email}</td>
                      <td className="p-4 text-stone-600 font-sans">{f.state || "—"}</td>
                      <td className="p-4 text-stone-500 font-sans text-xs">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-4 text-stone-800 font-sans">{f.product_count ?? 0}</td>
                      <td className="p-4 font-sans">
                        {f.verification_doc_url ? (
                          <a
                            href={f.verification_doc_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-emerald-800 underline text-xs"
                            data-testid={`view-id-${f.user_id}`}
                          >
                            View ID
                          </a>
                        ) : (
                          <span className="text-stone-400 text-xs">not uploaded</span>
                        )}
                      </td>
                      <td className="p-4">
                        {f.verified ? (
                          <Badge className="bg-emerald-800 text-stone-50">verified</Badge>
                        ) : (
                          <Badge className="bg-amber-700 text-stone-50">pending</Badge>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {!f.verified && (
                          <Button
                            size="sm"
                            data-testid={`verify-farmer-btn-${f.user_id}`}
                            onClick={() => verifyFarmer(f.user_id)}
                            disabled={!f.verification_doc_url}
                            className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"
                            title={!f.verification_doc_url ? "Farmer must upload ID first" : "Approve this farmer"}
                          >
                            <Check size={14} className="mr-1" /> Verify
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* SUPPORT — unified back-office inbox for messages from farmers / drivers / clients / wholesale */}
        <TabsContent value="support" className="mt-6 space-y-4">
          {supportTickets.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500">
              <LifeBuoy className="mx-auto text-emerald-800 mb-3" size={26} strokeWidth={1.5} />
              No support tickets yet. When a hobby farmer, driver, or client clicks <span className="font-semibold">Contact Support</span> on their dashboard, it shows up here.
            </div>
          ) : (
            supportTickets.map((t) => (
              <div key={t.id} data-testid={`support-ticket-${t.id}`} className="bg-white border border-stone-200 rounded-md p-5">
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-stone-800 text-stone-50 text-[10px] uppercase tracking-wider">{t.user_role || "user"}</Badge>
                      <Badge className="bg-amber-700/15 text-amber-800 text-[10px] uppercase tracking-wider">{t.category}</Badge>
                      <Badge
                        className={
                          t.status === "open" ? "bg-amber-700 text-stone-50"
                          : t.status === "answered" ? "bg-emerald-700 text-stone-50"
                          : "bg-stone-500 text-stone-50"
                        }
                      >
                        {t.status}
                      </Badge>
                    </div>
                    <div className="mt-2 font-serif text-lg text-stone-900">{t.subject}</div>
                    <div className="text-xs text-stone-500 mt-1 font-sans">
                      {t.user_name} · {t.user_email} · {new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                  {t.status !== "closed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => closeSupport(t.id)}
                      data-testid={`support-close-${t.id}`}
                      className="border-stone-300"
                    >
                      <XIcon size={14} className="mr-1.5" /> Close
                    </Button>
                  )}
                </div>

                <ul className="mt-4 space-y-3">
                  {(t.messages || []).map((m, i) => (
                    <li key={i} className={`text-sm font-sans p-3 rounded-md ${m.from === "admin" ? "bg-emerald-50 border border-emerald-100 ml-8" : "bg-stone-100 mr-8"}`}>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                        {m.from === "admin" ? "You" : m.from_name} · {new Date(m.at).toLocaleString()}
                      </div>
                      <div className="mt-1 text-stone-800 whitespace-pre-wrap">{m.body}</div>
                    </li>
                  ))}
                </ul>

                {t.status !== "closed" && (
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <Textarea
                      data-testid={`support-reply-${t.id}`}
                      rows={2}
                      placeholder="Write a reply…"
                      value={replyTexts[t.id] || ""}
                      onChange={(e) => setReplyTexts((r) => ({ ...r, [t.id]: e.target.value }))}
                      className="bg-stone-50 flex-1"
                    />
                    <Button
                      onClick={() => sendSupportReply(t.id)}
                      data-testid={`support-send-${t.id}`}
                      className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 sm:self-end"
                    >
                      <Send size={14} className="mr-1.5" /> Send
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </TabsContent>

        {/* ACTIVITY — admin alerts + recent events feed */}
        <TabsContent value="activity" className="mt-6 space-y-6">
          {!activity ? (
            <div className="bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500">
              Loading activity…
            </div>
          ) : (
            <>
              <section data-testid="activity-notifs">
                <h3 className="font-serif text-xl text-stone-900 mb-3">Your alerts</h3>
                {activity.notifications.length === 0 ? (
                  <div className="bg-white border border-stone-200 rounded-md p-6 font-sans text-sm text-stone-500">No alerts.</div>
                ) : (
                  <ul className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
                    {activity.notifications.map((n) => (
                      <li key={n.id || n.created_at} className="p-4 flex items-start gap-3">
                        <span className={`mt-1 w-2 h-2 rounded-full ${n.read ? "bg-stone-300" : "bg-emerald-600"}`} />
                        <div className="flex-1">
                          <div className="font-serif text-base text-stone-900">{n.title}</div>
                          {n.message && <div className="text-sm text-stone-600 font-sans mt-1">{n.message}</div>}
                          <div className="text-xs text-stone-400 mt-1 font-sans">
                            {new Date(n.created_at).toLocaleString()} · {n.kind}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section data-testid="activity-signups">
                <h3 className="font-serif text-xl text-stone-900 mb-3">Recent sign-ups</h3>
                <ul className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
                  {activity.recent_signups.map((u) => (
                    <li key={u.user_id} className="p-4 flex items-center gap-3 text-sm">
                      <Badge className="bg-stone-800 text-stone-50">{u.role}</Badge>
                      <span className="font-serif text-stone-900">{u.name}</span>
                      <span className="text-stone-500 font-sans truncate">{u.email}</span>
                      {u.manual_signup && <Badge className="bg-amber-700 text-stone-50 text-[10px]">manual</Badge>}
                      <span className="ml-auto text-xs text-stone-400 font-sans">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section data-testid="activity-recent-orders">
                <h3 className="font-serif text-xl text-stone-900 mb-3">Recent orders</h3>
                <ul className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
                  {activity.recent_orders.map((o) => (
                    <li key={o.order_id} className="p-4 flex items-center gap-3 text-sm">
                      <span className="font-serif text-stone-900">{o.product_title}</span>
                      <span className="text-stone-500 font-sans">{o.client_name}</span>
                      <Badge className={o.funds_verified ? "bg-emerald-800 text-stone-50" : "bg-amber-700 text-stone-50"}>
                        {o.funds_verified ? "verified" : "pending"}
                      </Badge>
                      <span className="ml-auto text-xs text-stone-400 font-sans">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </TabsContent>

        {/* DRIVERS — Applications */}
        <TabsContent value="drivers" className="mt-6 space-y-3">
          {driverApps.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">
              No driver applications yet.
            </div>
          ) : driverApps.map((a) => (
            <div key={a.user_id} data-testid={`drvapp-${a.user_id}`} className="bg-white border border-stone-200 rounded-md p-5">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <div className="font-serif text-lg text-stone-900">{a.full_name}</div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">
                    {a.email} · {a.phone} · License {a.license_number} ({a.license_state})
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">
                    Vehicle: {a.vehicle_year} {a.vehicle_make} {a.vehicle_model} · Plate {a.license_plate}
                    {a.years_driving ? ` · ${a.years_driving} yrs driving` : ""}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">
                    Payout: {a.payout_paypal || "—"}{a.payout_cashapp ? ` · CashApp ${a.payout_cashapp}` : ""}
                  </div>
                </div>
                <Badge className={
                  a.status === "approved" ? "bg-emerald-800 text-stone-50" :
                  a.status === "rejected" ? "bg-stone-500 text-stone-50" :
                  "bg-amber-700 text-stone-50"
                }>
                  {a.status}
                </Badge>
              </div>
              {a.status === "submitted" && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" data-testid={`drv-approve-${a.user_id}`} onClick={() => approveDriver(a.user_id)} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                    <CheckCircle2 size={14} className="mr-2" /> Approve driver
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`drv-reject-${a.user_id}`} onClick={() => rejectDriver(a.user_id)} className="border-stone-300">
                    <XCircle size={14} className="mr-2" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        {/* LEDGER */}
        <TabsContent value="ledger" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 uppercase text-stone-600 tracking-[0.15em] text-xs">
                <tr>
                  <th className="p-4 text-left">When</th>
                  <th className="p-4 text-left">User</th>
                  <th className="p-4 text-left">Role</th>
                  <th className="p-4 text-left">Order</th>
                  <th className="p-4 text-left">Amount</th>
                  <th className="p-4 text-left">Method</th>
                  <th className="p-4 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-stone-500 font-sans">No deposits yet.</td></tr>
                ) : ledger.map((l) => (
                  <tr key={l.id} className="border-t border-stone-100" data-testid={`ledger-${l.id}`}>
                    <td className="p-4 font-sans text-stone-600">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="p-4 font-sans text-stone-600">{l.user_id}</td>
                    <td className="p-4"><Badge className="bg-stone-800 text-stone-50">{l.user_role}</Badge></td>
                    <td className="p-4 font-sans text-stone-600">{l.order_id}</td>
                    <td className="p-4 font-serif text-stone-900">${Number(l.amount).toFixed(2)}</td>
                    <td className="p-4 font-sans text-stone-600">{l.payout_method || "—"}</td>
                    <td className="p-4"><Badge className="bg-emerald-800 text-stone-50">{l.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* USERS — with Report */}
        <TabsContent value="users" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 uppercase text-stone-600 tracking-[0.15em] text-xs">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Email</th>
                  <th className="p-4 text-left">Role</th>
                  <th className="p-4 text-left">State</th>
                  <th className="p-4 text-left">Verified</th>
                  <th className="p-4 text-left">Member</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-stone-100">
                    <td className="p-4 font-sans">{u.name}</td>
                    <td className="p-4 font-sans text-stone-600">{u.email}</td>
                    <td className="p-4"><Badge className="bg-stone-800 text-stone-50">{u.role}</Badge></td>
                    <td className="p-4 font-sans text-stone-600">{u.state || "—"}</td>
                    <td className="p-4 font-sans">{u.verified ? "Yes" : "No"}</td>
                    <td className="p-4 font-sans text-stone-600 text-xs">
                      {u.is_member ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                          {u.member_until ? `until ${String(u.member_until).slice(0, 10)}` : "active"}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-2 justify-end flex-wrap">
                        <GrantMembershipDialog
                          userId={u.user_id}
                          userName={u.name}
                          onGranted={loadAll}
                        />
                        {(u.role === "driver" || u.role === "farmer") && (
                          <ReportUserDialog
                            reportedUserId={u.user_id}
                            reportedUserName={u.name}
                            testidPrefix={`report-user-${u.user_id}`}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-6 space-y-3">
          {reports.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No reports filed.</div>
          ) : reports.map((r) => (
            <div key={r.id} className="bg-white border border-stone-200 p-5 rounded-md">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-serif text-lg text-stone-900">Report from {r.reporter_name}</div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">Against user: {r.reported_user_id}</div>
                </div>
                <Badge className="bg-amber-700 text-stone-50">{r.status}</Badge>
              </div>
              <p className="mt-2 text-sm text-stone-600 font-sans">{r.reason}</p>
            </div>
          ))}
        </TabsContent>

        {/* WHOLESALE */}
        <TabsContent value="wholesale" className="mt-6 space-y-3">
          {wholesale.length === 0 ? <div className="text-stone-500 font-sans">No applications.</div> : wholesale.map((w) => (
            <div key={w.id} className="bg-white border border-stone-200 rounded-md p-5">
              <div className="flex justify-between">
                <div>
                  <div className="font-serif text-lg text-stone-900">{w.business_name}</div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">{w.user_email} · {w.state} · {w.business_type}</div>
                </div>
                <Badge className="bg-amber-700 text-stone-50">{w.status}</Badge>
              </div>
              <div className="mt-2 text-sm text-stone-600 font-sans">Volume: {w.expected_volume} · Phone: {w.contact_phone}</div>
              {w.notes && <div className="mt-1 text-sm text-stone-500 font-sans">"{w.notes}"</div>}
            </div>
          ))}
        </TabsContent>

        {/* CONTACT MESSAGES */}
        <TabsContent value="contact-messages" className="mt-6 space-y-3">
          {contactMessages.length === 0 ? <div className="text-stone-500 font-sans">No contact messages.</div> : contactMessages.map((m) => (
            <div key={m.id} className="bg-white border border-stone-200 rounded-md p-5">
              <div className="font-serif text-lg text-stone-900">{m.subject}</div>
              <div className="text-xs text-stone-500 mt-1 font-sans">{m.name} · {m.email}</div>
              <p className="mt-2 text-sm text-stone-600 font-sans">{m.message}</p>
            </div>
          ))}
        </TabsContent>

        {/* TESTIMONIALS */}
        <TabsContent value="testimonials" className="mt-6 space-y-3">
          {testimonials.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No testimonials yet.</div>
          ) : testimonials.map((t) => (
            <div key={t.id} data-testid={`admin-tm-${t.id}`} className="bg-white border border-stone-200 p-5 rounded-md">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-serif text-lg text-stone-900">"{t.quote}"</div>
                  <div className="text-xs text-stone-500 mt-1 font-sans">
                    {t.name} · {t.role}{t.state ? ` · ${t.state}` : ""}
                    {t.farmer_name && <span className="text-emerald-800"> · about {t.farmer_name}</span>}
                    {t.rating && <span> · {t.rating}/5</span>}
                  </div>
                </div>
                <Badge className={t.approved ? "bg-emerald-800 text-stone-50" : "bg-amber-700 text-stone-50"}>
                  {t.approved ? "approved" : "pending"}
                </Badge>
              </div>
              <div className="mt-3 flex gap-2">
                {!t.approved && (
                  <Button data-testid={`approve-tm-${t.id}`} size="sm" onClick={() => approveTestimonial(t.id)} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                    <Check size={14} className="mr-2" /> Approve
                  </Button>
                )}
                <Button data-testid={`delete-tm-${t.id}`} size="sm" variant="outline" onClick={() => deleteTestimonial(t.id)} className="border-stone-300">
                  <XIcon size={14} className="mr-2" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* INBOX */}
        <TabsContent value="messages" className="mt-6">
          <Messages />
        </TabsContent>
      </Tabs>
    </div>
  );
}
